import { useMemo } from 'react';

import { getMsUntilNextSecond } from '#shared';

import { usePollingQuery } from '../../usePollingQuery';
import { sortHubsUsRegionsFirst } from '../helpers';
import { useGetHubsResourceReportQuery } from '../hubsResourceReport.api';
import { useStaleWhileEmpty } from '../useStaleWhileEmpty';

import type { HubReportStatus } from '#entities';
import type { PollingQueryResult } from '../types';

const TARGET_SECOND = 5;

const getHubsResourceReportDelay = () => getMsUntilNextSecond(TARGET_SECOND);

export const useQueryHubsResourceReport = (): PollingQueryResult<
  HubReportStatus[]
> => {
  const queryResult = useGetHubsResourceReportQuery(undefined, {
    pollingInterval: 0,
  });

  const pollingResult = usePollingQuery(queryResult, {
    getDelay: getHubsResourceReportDelay,
  });

  const stale = useStaleWhileEmpty(
    {
      data: pollingResult.data?.data ?? [],
      generatedAt: pollingResult.data?.generatedAt ?? null,
    },
    pollingResult.lastReceiveTime?.getTime() ?? null,
  );

  return useMemo(
    () => ({
      data: sortHubsUsRegionsFirst(stale.data),
      generatedAt: stale.generatedAt,
      isStale: stale.isStale,
      validation: null, // TODO: add validation when schema validation is added
      isLoading: pollingResult.isLoading,
      isFetching: pollingResult.isFetching,
      isError: pollingResult.isError,
      error: pollingResult.error,
      lastSentTime: pollingResult.lastSentTime,
      lastReceiveTime: pollingResult.lastReceiveTime,
    }),
    [pollingResult, stale],
  );
};
